import React, { useState } from 'react';
import { Shield, Languages, Bell, User as LucideUser, LogOut, Sun, Moon } from 'lucide-react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext'; // Updated import path
import { useTheme } from '../contexts/ThemeContext';
import { useNotifications } from '../contexts/NotificationContext';
import ProfileModal from './ProfileModal'; 
import NotificationPanel from './NotificationPanel'; // Assuming NotificationPanel is in the same directory

const AppHeader: React.FC = () => {
  const { currentUser, logout } = useAuth(); // Removed authLoading as it's not used here
  const { language, setLanguage, t } = useLanguage();
  const { theme, toggleTheme } = useTheme();
  const { getUnreadCount } = useNotifications();
  const [showProfile, setShowProfile] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const unreadCount = currentUser ? getUnreadCount(currentUser.id) : 0;
  const isProjectPage = location.pathname.startsWith('/project/');

  return (
    <header className="app-header border">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex justify-between items-center app-header-inner">
          <div className="flex items-center app-header-title-block">
            <Shield className="w-8 h-8 text-blue-600 mr-3" />
            <Link to="/" className="text-xl font-bold text-gray-900 hover:text-blue-600 transition-colors" style={{ textDecoration: 'none' }}>{t('app.title')}</Link>
          </div>
          <div className="flex items-center space-x-4">
            {currentUser ? (
              <>
                <div className="text-right app-header-user-info">
                  <p className="text-xs font-semibold custom-user-name">{currentUser?.fullName}</p>
                  <p className="text-sm text-gray-500 custom-user-role">
                    {currentUser?.role ? t(`user.${currentUser.role}`) : ''} • {currentUser?.team}
                  </p>
                </div>
                <div className="flex items-center space-x-1 pl-3 app-header-actions border-l-gray-200">
                  <button
                    onClick={toggleTheme}
                    className="p-2 text-gray-600 hover:text-gray-900 rounded-full hover:bg-gray-100 transition-colors"
                    title={t('action.toggleTheme', {defaultValue: theme === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode'})}
                  >
                    {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
                  </button>
                  <button
                    onClick={() => setLanguage(language === 'th' ? 'en' : 'th')}
                    className="p-2 text-gray-600 hover:text-gray-900 rounded-full hover:bg-gray-100 transition-colors"
                    title={t('action.changeLanguage', {defaultValue: "Change Language"})}
                  >
                    <Languages className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => setShowNotifications(true)}
                    className="p-2 text-gray-600 hover:text-gray-900 rounded-full hover:bg-gray-100 transition-colors relative"
                    title={t('notification.title')}
                    style={{ overflow: 'visible' }}
                  >
                    <Bell className="w-5 h-5" />
                    {unreadCount > 0 && (
                      <span
                        className="absolute notification-badge-bg notification-badge-ring text-white rounded-full flex items-center justify-center font-bold"
                        style={{
                          top: '0px',
                          right: '0px',
                          minWidth: '16px',
                          height: '16px',
                          padding: '0 4px',
                          fontSize: '0.625rem',
                          lineHeight: 1,
                          zIndex: 10,
                        }}
                      >
                        {unreadCount > 99 ? '99+' : unreadCount}
                      </span>
                    )}
                  </button>
                  <button 
                    onClick={() => setShowProfile(true)} 
                    className="p-2 text-gray-600 hover:text-gray-900 rounded-full hover:bg-gray-100 transition-colors" 
                    title={t('user.profile')}
                  >
                    <LucideUser className="w-5 h-5" />
                  </button>
                  <button 
                    onClick={() => { logout(); navigate('/login'); }} 
                    className="p-2 text-gray-600 hover:text-red-600 rounded-full hover:bg-red-50 transition-colors" 
                    title={t('auth.logout')}
                  >
                    <LogOut className="w-5 h-5" />
                  </button>
                </div>
              </>
            ) : (
              <div className="flex items-center space-x-3">
                <button
                  onClick={toggleTheme}
                  className="p-2 text-gray-600 hover:text-gray-900 rounded-full hover:bg-gray-100 transition-colors"
                  title={t('action.toggleTheme', {defaultValue: theme === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode'})}
                >
                  {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
                </button>
                <button
                  onClick={() => setLanguage(language === 'th' ? 'en' : 'th')}
                  className="p-2 text-gray-600 hover:text-gray-900 rounded-full hover:bg-gray-100 transition-colors"
                  title={t('action.changeLanguage', {defaultValue: "Change Language"})}
                >
                  <Languages className="w-5 h-5" />
                </button>
                {isProjectPage ? (
                  <button 
                    onClick={() => navigate('/login', { state: { from: location } })} 
                    className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 transition-colors font-medium text-sm shadow-sm ml-4"
                  >
                    Login to Edit
                  </button>
                ) : (
                  <button 
                    onClick={() => navigate('/login')} 
                    className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 transition-colors font-medium text-sm shadow-sm ml-4"
                  >
                    Login
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      {showProfile && <ProfileModal onClose={() => setShowProfile(false)} />}
      {showNotifications && <NotificationPanel onClose={() => setShowNotifications(false)} />}
    </header>
  );
};

export default AppHeader;
